/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Pure rules for the floating "go to the comparison" call-to-action
 * (<FloatingCompareCta>): on which routes it may appear, where it points, the
 * scroll mark it appears at, when the footer is in view, and how the gates
 * combine. No React, no i18n, no DOM — the component only feeds these rules
 * what the browser reports (scroll position, the footer's IntersectionObserver
 * records, the stored dismissal), so every decision can be unit-tested.
 */

import { getComparerHref, type CompareLanguage } from './compareAnchor';
import { splitLanguageFromPath } from './gtag';
import { PROVIDERS_SEGMENT } from './providerRoutes.js';

/** Which rendering of the CTA is on screen: card (lg and up) or pill (below). */
export type FloatingCtaVariant = 'desktop' | 'mobile';

/**
 * Route gate. The CTA exists to pull readers of long content pages back into
 * the comparer, so it stands down wherever that would be noise or wrong:
 *
 * - the homepage — it *is* the comparer, and the comparer floats its own
 *   control on the bottom edge there (the shortlist tray,
 *   comparison/ShortlistTray.tsx);
 * - `/vergelijk` and `/vergelijk/<matchup>` — the multi-compare and
 *   head-to-head tools are comparison pages themselves and already carry
 *   "back to the comparison" links;
 * - every `/partner*` route — audience is providers, not consumers, and the
 *   portal pins its own unsaved-changes bar to the bottom edge;
 * - `/geen-laadpaal` and every address under it — the no-charger funnel;
 *   sending someone without a charge point to compare ERE payouts contradicts
 *   the page, which carries the aanvraagformulier. (The twelve
 *   `/geen-laadpaal/<provincie>` pages went on 2026-10-07; an address under
 *   the hub only hops to it now.)
 *
 * On the mobile variant two more routes are out, because they already float
 * something on the same bottom-centre spot: article pages (share pill) and
 * provider profiles (sticky sign-up bar below `sm`, compare CTA in the header
 * band above it). The desktop card sits bottom-right and coexists with the
 * centred article pill, so desktop keeps both.
 *
 * Unknown/404 paths are allowed through here; the length gate in the
 * component keeps short pages clean.
 */
export function isFloatingCtaEligible(pathname: string, variant: FloatingCtaVariant): boolean {
  const { path } = splitLanguageFromPath(pathname);
  if (path === '/') return false;
  if (path === '/vergelijk' || path.startsWith('/vergelijk/')) return false;
  if (path === '/partner' || path.startsWith('/partner-') || path.startsWith('/partner/')) {
    return false;
  }
  if (path === '/geen-laadpaal' || path.startsWith('/geen-laadpaal/')) return false;
  if (variant === 'mobile') {
    if (path.startsWith('/kennisbank/')) return false;
    if (path.startsWith(`/${PROVIDERS_SEGMENT}/`)) return false;
  }
  return true;
}

/**
 * Where the CTA sends the visitor: where the comparison starts (Stap 1 —
 * Bereken — at the top of the homepage from `sm`, the results on a phone; see
 * getComparerHref), language-prefixed like every other link.
 * On the business landing page it carries the business audience preset — the
 * page's own CTAs do the same — so a reader who came in via /zakelijk starts
 * with the zakelijk toggle set, not the default.
 */
export function floatingCtaHref(pathname: string, language: CompareLanguage): string {
  const { path } = splitLanguageFromPath(pathname);
  return getComparerHref(language, path === '/zakelijk' ? 'Zakelijk' : undefined);
}

/** Scroll distance at which the CTA appears: about a screen, never less than this many px. */
export const SHOW_AT_MIN_PX = 320;
export const SHOW_AT_VIEWPORT_FRACTION = 0.8;
/** Hide again this many px *above* the show mark — keeps a visitor idling on the line from flickering it. */
export const SHOW_HYSTERESIS_PX = 120;

/**
 * The scroll gate: the visitor is about a screen down the page. Once in, the
 * CTA stays in until the visitor is back {@link SHOW_HYSTERESIS_PX} above the
 * mark, so idling on the line does not flicker it.
 */
export function nextScrolledIn(was: boolean, scrollY: number, viewportHeight: number): boolean {
  const showAt = Math.max(SHOW_AT_MIN_PX, Math.round(viewportHeight * SHOW_AT_VIEWPORT_FRACTION));
  return was ? scrollY > showAt - SHOW_HYSTERESIS_PX : scrollY > showAt;
}

/**
 * The footer gate, fed by an IntersectionObserver on the site footer (no root
 * margin, threshold 0): the footer counts as in view from its first pixel in
 * the viewport, so the CTA steps aside BEFORE it can cover anything of it —
 * the legal row bottom-right sits exactly where the desktop card floats. Until
 * 2026-10-10 the component compared the footer's top with the viewport bottom
 * minus 96 px on every scroll event, which (a) let the card cover the
 * footer's first 96 px while it entered (36 px of overlap at 1280×800, 48–50 px
 * on a 390×844 phone) and (b) never re-ran when the page grew or shrank
 * without a scroll — a collapsing block between the reader and the footer
 * pulled the footer under the card and left it there (140 px of overlap at
 * 1280×800) until the visitor scrolled or closed it. The observer reports
 * every geometry change, scroll or not.
 *
 * One callback can carry several records for the one target when crossings
 * queue up; the newest (last) one is the current state. No records keeps the
 * previous answer.
 */
export function footerInViewFromEntries(
  entries: ReadonlyArray<{ isIntersecting: boolean }>,
  was: boolean
): boolean {
  return entries.length > 0 ? entries[entries.length - 1].isIntersecting : was;
}

/**
 * The same question asked of one rect, once, when the observer starts: its
 * first record arrives a frame later, and a gate that opens with the footer
 * already on screen (an article page widened across `lg` at its footer: only
 * the desktop card is allowed there) would otherwise mount the card for that
 * frame. Same edge as the observer: one pixel inside the viewport counts.
 */
export function footerRectInView(
  rect: { top: number; bottom: number },
  viewportHeight: number
): boolean {
  return rect.top < viewportHeight && rect.bottom > 0;
}

/** Everything that decides whether the CTA is on screen. */
export interface FloatingCtaGates {
  /** The route allows this variant ({@link isFloatingCtaEligible}). */
  eligible: boolean;
  /** The page is long enough (hooks/useIsLongPage.ts). */
  isLong: boolean;
  /** About a screen down ({@link nextScrolledIn}). */
  scrolledIn: boolean;
  /** Any pixel of the site footer is in the viewport ({@link footerInViewFromEntries}). */
  footerInView: boolean;
  /** Closed this browser session. */
  dismissed: boolean;
  /** A cookie choice is recorded — until then the banner owns the bottom edge. */
  hasConsent: boolean;
}

/**
 * The CTA is on screen only when every gate agrees. The footer gate is the
 * only one that comes back by itself: when the footer leaves the viewport the
 * card returns, while a dismissal holds for the rest of the session.
 */
export function isFloatingCtaVisible(g: FloatingCtaGates): boolean {
  return g.eligible && g.isLong && g.scrolledIn && !g.footerInView && !g.dismissed && g.hasConsent;
}
